/**
 * Street Shark Vehicle Selector
 * Handles Year/Make/Model/Trim selection with NHTSA API
 */

class VehicleSelector {
  constructor() {
    this.apiBase = 'https://vpic.nhtsa.dot.gov/api/vehicles';
    this.selectedYear = null;
    this.selectedMake = null;
    this.selectedModel = null;
    
    this.init();
  }
  
  init() {
    // Wait for DOM to be ready
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', () => this.setup());
    } else {
      this.setup();
    }
  }
  
  setup() {
    this.attachEventListeners();
  }
  
  attachEventListeners() {
    // Year selection
    const yearDropdowns = document.querySelectorAll('#vehicle-year');
    yearDropdowns.forEach(dropdown => {
      dropdown.addEventListener('change', (e) => this.handleYearChange(e));
    });
    
    // Make selection
    const makeDropdowns = document.querySelectorAll('#vehicle-make');
    makeDropdowns.forEach(dropdown => {
      dropdown.addEventListener('change', (e) => this.handleMakeChange(e));
    });
    
    // Model selection
    const modelDropdowns = document.querySelectorAll('#vehicle-model');
    modelDropdowns.forEach(dropdown => {
      dropdown.addEventListener('change', (e) => this.handleModelChange(e));
    });
    
    // Form submissions
    const forms = document.querySelectorAll('.vehicle-form');
    forms.forEach(form => {
      form.addEventListener('submit', (e) => this.handleFormSubmit(e));
    });
  }
  
  async handleYearChange(e) {
    const year = e.target.value;
    this.selectedYear = year;
    
    const form = e.target.closest('form');
    const makeDropdown = form.querySelector('#vehicle-make');
    const modelDropdown = form.querySelector('#vehicle-model');
    const trimDropdown = form.querySelector('#vehicle-trim');
    
    // Reset dependent dropdowns
    this.resetDropdown(modelDropdown, 'Model');
    this.resetDropdown(trimDropdown, 'Trim (Optional)');
    
    if (!year) {
      this.resetDropdown(makeDropdown, 'Make');
      return;
    }
    
    // Show loading state
    makeDropdown.innerHTML = '<option value="">Loading makes...</option>';
    makeDropdown.disabled = true;
    
    try {
      const makes = await this.fetchMakes(year);
      this.populateMakes(makeDropdown, makes);
    } catch (error) {
      console.error('Error fetching makes:', error);
      makeDropdown.innerHTML = '<option value="">Error loading makes</option>';
    }
    
    makeDropdown.disabled = false;
  }
  
  async handleMakeChange(e) {
    const make = e.target.value;
    this.selectedMake = make;
    
    const form = e.target.closest('form');
    const modelDropdown = form.querySelector('#vehicle-model');
    const trimDropdown = form.querySelector('#vehicle-trim');
    
    // Reset dependent dropdown
    this.resetDropdown(trimDropdown, 'Trim (Optional)');
    
    if (!make || !this.selectedYear) {
      this.resetDropdown(modelDropdown, 'Model');
      return;
    }
    
    modelDropdown.innerHTML = '<option value="">Loading models...</option>';
    modelDropdown.disabled = true;
    
    try {
      const models = await this.fetchModels(this.selectedYear, make);
      this.populateModels(modelDropdown, models);
    } catch (error) {
      console.error('Error fetching models:', error);
      modelDropdown.innerHTML = '<option value="">Error loading models</option>';
    }
    
    modelDropdown.disabled = false;
  }
  
  handleModelChange(e) {
    const model = e.target.value;
    this.selectedModel = model;
    
    // Trim dropdown remains optional and empty for now
    // You can add trim fetching logic here if needed
  }
  
  async fetchMakes(year) {
    try {
      const response = await fetch(
        `${this.apiBase}/GetMakesForVehicleType/car?format=json`
      );
      const data = await response.json();
      
      if (data.Results) {
        return data.Results
          .map(item => item.MakeName)
          .filter(make => make) // Remove empty values
          .sort();
      }
      
      return this.getFallbackMakes();
    } catch (error) {
      console.error('API Error:', error);
      return this.getFallbackMakes();
    }
  }
  
  async fetchModels(year, make) {
    try {
      const response = await fetch(
        `${this.apiBase}/GetModelsForMakeYear/make/${encodeURIComponent(make)}/modelyear/${year}?format=json`
      );
      const data = await response.json();
      
      if (data.Results && data.Results.length > 0) {
        const models = [...new Set(data.Results.map(item => item.Model_Name))];
        return models.filter(model => model).sort();
      }
      
      return [];
    } catch (error) {
      console.error('API Error:', error);
      return [];
    }
  }
  
  populateMakes(dropdown, makes) {
    dropdown.innerHTML = '<option value="">Select Make</option>';
    
    makes.forEach(make => {
      const option = document.createElement('option');
      option.value = make;
      option.textContent = make;
      dropdown.appendChild(option);
    });
  }
  
  populateModels(dropdown, models) {
    dropdown.innerHTML = '<option value="">Select Model</option>';
    
    if (models.length === 0) {
      dropdown.innerHTML = '<option value="">No models found</option>';
      return;
    }
    
    models.forEach(model => {
      const option = document.createElement('option');
      option.value = model;
      option.textContent = model;
      dropdown.appendChild(option);
    });
  }
  
  resetDropdown(dropdown, label) {
    if (dropdown) {
      dropdown.innerHTML = `<option value="">${label}</option>`;
      dropdown.disabled = false;
    }
  }
  
  handleFormSubmit(e) {
    e.preventDefault();
    
    const form = e.target;
    const year = form.querySelector('#vehicle-year').value;
    const make = form.querySelector('#vehicle-make').value;
    const model = form.querySelector('#vehicle-model').value;
    const trim = form.querySelector('#vehicle-trim')?.value || '';
    
    if (!year || !make || !model) {
      alert('Please select Year, Make, and Model');
      return;
    }
    
    // Build search query
    let searchQuery = `${year} ${make} ${model}`;
    if (trim) {
      searchQuery += ` ${trim}`;
    }
    
    // Redirect to search with query
    const searchParams = new URLSearchParams({
      q: searchQuery,
      type: 'product'
    });
    
    window.location.href = `/search?${searchParams.toString()}`;
  }
  
  getFallbackMakes() {
    return [
      'Acura', 'Audi', 'BMW', 'Buick', 'Cadillac', 'Chevrolet', 'Chrysler',
      'Dodge', 'Ford', 'GMC', 'Honda', 'Hyundai', 'Infiniti', 'Jeep',
      'Kia', 'Lexus', 'Lincoln', 'Mazda', 'Mercedes-Benz', 'Nissan',
      'Ram', 'Subaru', 'Tesla', 'Toyota', 'Volkswagen', 'Volvo'
    ];
  }
}

// Initialize when script loads
new VehicleSelector();